<script lang="ts" setup>
  import { DatePicker } from '@arco-design/web-vue';
  const { MonthPicker, QuarterPicker, RangePicker, WeekPicker, YearPicker } = DatePicker;
  // import '@arco-design/web-vue/dist/arco.css';
  import { PropType, computed, ref, watch } from 'vue';
  import type { Shortcuts } from './date-picker';
  defineOptions({
    name: 'IDatePicker',
  });

  // 文档 https://arco.design/vue/component/date-picker
  const props = defineProps({
    modelValue: {
      type: [String, Array] as PropType<string | string[]>,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    startPlaceholder: {
      type: String,
      default: '',
    },
    endPlaceholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    size: {
      type: String,
    },
    type: {
      type: String,
      default: 'date', //'year' | 'yearrange' |'month' | 'monthrange' | 'date' | 'daterange' | 'datetime' | 'datetimerange' | 'week' | 'weekrange' | 'quarter' | 'quarterrange'
    },
    format: {
      type: String,
      default: null,
    },
    valueFormat: {
      type: String,
      default: null,
    },
    shortcuts: {
      type: Array as PropType<Shortcuts[]>,
      default: () => [],
    },
    disabledDate: {
      type: Function,
      default: () => false,
    },
    rangeSeparator: {
      type: String,
      default: '-',
    },
  });
  const inputValue = ref(props.modelValue);
  const emit = defineEmits(['change', 'clear', 'select', 'update:modelValue']);
  function onChange(value: any) {
    emit('change', value);
  }
  function onSelect(value: any) {
    emit('select', value);
  }
  const realShortcuts = computed(() => {
    return props.shortcuts.map(item => {
      return {
        label: item.text,
        value: item.value,
      };
    });
  });

  const defaultProps = computed(() => {
    // 获取当前时间作为基准
    const baseDate = new Date();

    // 当天结束时间 (23:59:59.999)
    const now = new Date(baseDate);
    now.setHours(23, 59, 59, 999);

    // 7天前的开始时间 (00:00:00.000)
    const date1 = new Date(baseDate);
    date1.setDate(date1.getDate() - 7);
    date1.setHours(0, 0, 0, 0);

    // 1个月前的开始时间 (00:00:00.000)
    const date2 = new Date(baseDate);
    date2.setMonth(date2.getMonth() - 1);
    date2.setHours(0, 0, 0, 0);

    // 1年前的开始时间 (00:00:00.000)
    const date3 = new Date(baseDate);
    date3.setFullYear(date3.getFullYear() - 1);
    date3.setHours(0, 0, 0, 0);

    switch (props.type) {
      case 'year':
        return {
          format: props.format || 'YYYY',
          valueFormat: props.valueFormat || 'YYYY',
          shortcuts: realShortcuts.value || [],
        };
      case 'yearrange':
        return {
          format: props.format || 'YYYY',
          valueFormat: props.valueFormat || 'YYYY',
          shortcuts: realShortcuts.value || [],
        };
      case 'month':
        return {
          format: props.format || 'YYYY-MM',
          valueFormat: props.valueFormat || 'YYYY-MM',
          shortcuts: realShortcuts.value || [],
        };
      case 'monthrange':
        return {
          format: props.format || 'YYYY-MM',
          valueFormat: props.valueFormat || 'YYYY-MM',
          shortcuts: realShortcuts.value || [],
        };
      case 'week':
        return {
          format: props.format || 'gggg-wo',
          valueFormat: props.valueFormat || 'gggg-wo',
          shortcuts: realShortcuts.value || [],
        };
      case 'weekrange':
        return {
          format: props.format || 'gggg-wo',
          valueFormat: props.valueFormat || 'gggg-wo',
          shortcuts: realShortcuts.value || [],
        };
      case 'quarter':
        return {
          format: props.format || 'YYYY-[Q]Q',
          valueFormat: props.valueFormat || 'YYYY-[Q]Q',
          shortcuts: realShortcuts.value || [],
        };
      case 'quarterrange':
        return {
          format: props.format || 'YYYY-[Q]Q',
          valueFormat: props.valueFormat || 'YYYY-[Q]Q',
          shortcuts: realShortcuts.value || [],
        };
      case 'date':
        return {
          format: props.format || 'YYYY-MM-DD',
          valueFormat: props.valueFormat || 'YYYY-MM-DD',
          shortcuts:
            realShortcuts.value.length === 0
              ? [
                  {
                    label: '7天前',
                    value: date1,
                  },
                  {
                    label: '1月前',
                    value: date2,
                  },
                  {
                    label: '1年前',
                    value: date3,
                  },
                ]
              : realShortcuts.value,
        };
      case 'daterange':
        return {
          format: props.format || 'YYYY-MM-DD',
          valueFormat: props.valueFormat || 'YYYY-MM-DD',
          shortcuts:
            realShortcuts.value.length === 0
              ? [
                  {
                    label: '最近7天',
                    value: [date1, now],
                  },
                  {
                    label: '最近1月',
                    value: [date2, now],
                  },
                  {
                    label: '最近1年',
                    value: [date3, now],
                  },
                ]
              : realShortcuts.value,
        };
      case 'datetime':
        return {
          format: props.format || 'YYYY-MM-DD HH:mm:ss',
          valueFormat: props.valueFormat || 'YYYY-MM-DD HH:mm:ss',
          shortcuts:
            realShortcuts.value.length === 0
              ? [
                  {
                    label: '7天前',
                    value: date1,
                  },
                  {
                    label: '1月前',
                    value: date2,
                  },
                  {
                    label: '1年前',
                    value: date3,
                  },
                ]
              : realShortcuts.value,
        };
      case 'datetimerange':
        return {
          format: props.format || 'YYYY-MM-DD HH:mm:ss',
          valueFormat: props.valueFormat || 'YYYY-MM-DD HH:mm:ss',
          shortcuts:
            realShortcuts.value.length === 0
              ? [
                  {
                    label: '最近7天',
                    value: [date1, now],
                  },
                  {
                    label: '最近1月',
                    value: [date2, now],
                  },
                  {
                    label: '最近1年',
                    value: [date3, now],
                  },
                ]
              : realShortcuts.value,
        };
      default:
        return {
          format: props.format || 'YYYY-MM-DD',
          valueFormat: props.valueFormat || 'YYYY-MM-DD',
          shortcuts: [],
        };
    }
  });

  const realPlaceholder = computed(() => {
    if (props.startPlaceholder && props.endPlaceholder) {
      return [props.startPlaceholder, props.endPlaceholder];
    } else {
      return props.placeholder;
    }
  });
  watch(
    () => props.modelValue,
    val => {
      inputValue.value = val;
    },
  );
  watch(inputValue, val => {
    emit('update:modelValue', val);
  });
</script>

<template>
  <div class="i-date-picker">
    <YearPicker
      v-if="type === 'year'"
      :disabled="disabled"
      v-model="inputValue"
      :allow-clear="clearable"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <range-picker
      v-else-if="type === 'yearrange'"
      mode="year"
      v-model="inputValue"
      :separator="rangeSeparator"
      :allow-clear="clearable"
      :disabled="disabled"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <MonthPicker
      v-else-if="type === 'month'"
      v-model="inputValue"
      :allow-clear="clearable"
      :disabled="disabled"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <range-picker
      v-else-if="type === 'monthrange'"
      mode="month"
      v-model="inputValue"
      :separator="rangeSeparator"
      :allow-clear="clearable"
      :disabled="disabled"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <DatePicker
      v-else-if="type === 'date'"
      v-model="inputValue"
      :disabled-date="disabledDate"
      :allow-clear="clearable"
      :disabled="disabled"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <range-picker
      v-else-if="type === 'daterange'"
      mode="date"
      v-model="inputValue"
      :separator="rangeSeparator"
      :disabled-date="disabledDate"
      :allow-clear="clearable"
      :disabled="disabled"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <WeekPicker
      v-else-if="type === 'week'"
      v-model="inputValue"
      :allow-clear="clearable"
      :disabled="disabled"
      :placeholder="realPlaceholder"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <range-picker
      v-else-if="type === 'weekrange'"
      mode="week"
      v-model="inputValue"
      :separator="rangeSeparator"
      :allow-clear="clearable"
      :disabled="disabled"
      :placeholder="realPlaceholder"
      @select="onSelect"
      @change="onChange"
    />
    <QuarterPicker
      v-else-if="type === 'quarter'"
      v-model="inputValue"
      :allow-clear="clearable"
      :disabled="disabled"
      :placeholder="realPlaceholder"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <range-picker
      v-else-if="type === 'quarterrange'"
      mode="quarter"
      v-model="inputValue"
      :separator="rangeSeparator"
      :allow-clear="clearable"
      :disabled="disabled"
      :placeholder="realPlaceholder"
      @select="onSelect"
      @change="onChange"
    />
    <DatePicker
      v-else-if="type === 'datetime'"
      showTime
      v-model="inputValue"
      :disabled-date="disabledDate"
      :allow-clear="clearable"
      :disabled="disabled"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
    <range-picker
      v-else-if="type === 'datetimerange'"
      showTime
      v-model="inputValue"
      :separator="rangeSeparator"
      :disabled-date="disabledDate"
      :allow-clear="clearable"
      :disabled="disabled"
      :exchange-time="false"
      :format="defaultProps.format"
      :placeholder="realPlaceholder"
      :value-format="defaultProps.valueFormat"
      :shortcuts="defaultProps.shortcuts"
      @select="onSelect"
      @change="onChange"
    />
  </div>
</template>
